import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    totalUsers: 0
  });

  const [creditStats, setCreditStats] = useState({
    activeCreditClients: 0,
    totalCreditLimit: 0,
    totalCreditUsed: 0,
    totalCreditAvailable: 0,
    maxedOutClients: 0,
    nearLimitClients: 0,
    overdueAccounts: 0,
    overdueAmount: 0,
    totalPending: 0,
    wholesaleClients: 0
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [ordersRes, productsRes, creditRes] = await Promise.all([
          api.get('/orders/admin/all').catch(() => ({ data: { data: [] } })),
          api.get('/products').catch(() => ({ data: { data: [] } })),
          api.get('/auth/credit-stats').catch(() => ({ data: { data: {} } }))
        ]);

        const orders = ordersRes.data.data || [];
        const products = productsRes.data.data || [];

        setStats({
          totalOrders: orders.length,
          pendingOrders: orders.filter(o => o.status === 'pendiente').length,
          deliveredOrders: orders.filter(o => o.status === 'entregado').length,
          totalRevenue: orders
            .filter(o => o.status === 'entregado')
            .reduce((sum, o) => sum + Number(o.total_usd || 0), 0),
          totalProducts: products.length,
          totalUsers: new Set(orders.map(o => o.user_id)).size
        });

        if (creditRes.data?.data) {
          setCreditStats(prev => ({ ...prev, ...creditRes.data.data }));
        }

        setRecentOrders(orders.slice(0, 5));

        const productSales = {};
        orders.forEach(order => {
          (order.items || []).forEach(item => {
            productSales[item.name] = (productSales[item.name] || 0) + item.quantity;
          });
        });
        const sorted = Object.entries(productSales)
          .map(([name, qty]) => ({ name, qty }))
          .sort((a, b) => b.qty - a.qty)
          .slice(0, 5);
        setTopProducts(sorted);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: 'Pedidos totales', value: stats.totalOrders, icon: '📋', color: 'from-blue-500 to-blue-600', link: '/admin/pedidos' },
    { label: 'Pendientes', value: stats.pendingOrders, icon: '⏳', color: 'from-yellow-500 to-yellow-600', link: '/admin/pedidos' },
    { label: 'Entregados', value: stats.deliveredOrders, icon: '✅', color: 'from-green-500 to-green-600', link: '/admin/pedidos' },
    { label: 'Ingresos', value: `$${stats.totalRevenue.toFixed(2)}`, icon: '💰', color: 'from-gold to-gold-dark', link: '/admin/pedidos' }
  ];

  const statusColors = {
    pendiente: 'bg-yellow-100 text-yellow-700',
    aceptado: 'bg-blue-100 text-blue-700',
    preparando: 'bg-purple-100 text-purple-700',
    en_camino: 'bg-orange-100 text-orange-700',
    entregado: 'bg-green-100 text-green-700',
    cancelado: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-500">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  const creditUsagePercentage = creditStats.totalCreditLimit > 0
    ? (creditStats.totalCreditUsed / creditStats.totalCreditLimit) * 100
    : 0;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold mb-2">📊 Dashboard</h1>
        <p className="text-gray-500">Resumen general de tu panadería</p>
      </div>

      {/* Tarjetas de estadísticas generales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, i) => (
          <Link
            key={i}
            to={card.link}
            className={`bg-gradient-to-br ${card.color} text-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-3xl">{card.icon}</span>
            </div>
            <h3 className="text-sm opacity-90 mb-1">{card.label}</h3>
            <p className="text-3xl font-bold">{card.value}</p>
          </Link>
        ))}
      </div>

      {/* ============================================================ */}
      {/* 💰 SECCIÓN DE CRÉDITO */}
      {/* ============================================================ */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-serif font-bold">💰 Línea de Crédito</h2>
          <Link to="/admin/usuarios" className="text-gold text-sm hover:underline">
            Ver clientes →
          </Link>
        </div>

        {/* Alertas */}
        {creditStats.maxedOutClients > 0 && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl mb-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🚨</span>
              <div>
                <p className="font-semibold text-red-800">
                  {creditStats.maxedOutClients} cliente(s) con crédito agotado
                </p>
                <p className="text-sm text-red-700">
                  No pueden usar crédito hasta que paguen su saldo
                </p>
              </div>
            </div>
          </div>
        )}

        {creditStats.overdueAccounts > 0 && (
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-xl mb-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-semibold text-orange-800">
                  {creditStats.overdueAccounts} cuenta(s) vencida(s) por ${creditStats.overdueAmount.toFixed(2)}
                </p>
                <p className="text-sm text-orange-700">
                  Clientes con pagos atrasados
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tarjetas de crédito */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg">
            <div className="text-3xl mb-2">👥</div>
            <h3 className="text-sm opacity-90 mb-1">Clientes con crédito</h3>
            <p className="text-3xl font-bold">{creditStats.activeCreditClients}</p>
            <p className="text-xs opacity-80 mt-1">
              {creditStats.wholesaleClients} mayoristas en total
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg">
            <div className="text-3xl mb-2">💵</div>
            <h3 className="text-sm opacity-90 mb-1">Crédito disponible</h3>
            <p className="text-3xl font-bold">${creditStats.totalCreditAvailable.toFixed(2)}</p>
            <p className="text-xs opacity-80 mt-1">
              De ${creditStats.totalCreditLimit.toFixed(2)} otorgados
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-2xl shadow-lg">
            <div className="text-3xl mb-2">📊</div>
            <h3 className="text-sm opacity-90 mb-1">Crédito usado</h3>
            <p className="text-3xl font-bold">${creditStats.totalCreditUsed.toFixed(2)}</p>
            <p className="text-xs opacity-80 mt-1">
              Dinero que te deben
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-2xl shadow-lg">
            <div className="text-3xl mb-2">⏰</div>
            <h3 className="text-sm opacity-90 mb-1">Por cobrar</h3>
            <p className="text-3xl font-bold">${creditStats.totalPending.toFixed(2)}</p>
            <p className="text-xs opacity-80 mt-1">
              {creditStats.overdueAccounts} vencida(s)
            </p>
          </div>
        </div>

        {/* Barra de progreso del crédito */}
        {creditStats.totalCreditLimit > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mt-4">
            <div className="flex justify-between items-center mb-3">
              <span className="font-semibold text-gray-700">Uso del crédito otorgado</span>
              <span className="text-sm text-gray-500">
                {creditUsagePercentage.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  creditUsagePercentage > 80
                    ? 'bg-red-500'
                    : creditUsagePercentage > 50
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(100, creditUsagePercentage)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>Usado: ${creditStats.totalCreditUsed.toFixed(2)}</span>
              <span>Disponible: ${creditStats.totalCreditAvailable.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Pedidos recientes y Top productos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">📋 Pedidos recientes</h2>
            <Link to="/admin/pedidos" className="text-gold text-sm hover:underline">Ver todos →</Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📭</div>
              <p>No hay pedidos todavía</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(order => (
                <div key={order.id} className="flex justify-between items-center py-3 border-b border-gray-50 last:border-0">
                  <div className="flex-1">
                    <div className="font-medium text-primary">#{order.order_number}</div>
                    <div className="text-xs text-gray-500">
                      {order.customer_info?.name || 'Cliente'} · {new Date(order.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gold">${Number(order.total_usd).toFixed(2)}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[order.status]}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">🏆 Top productos</h2>
            <Link to="/admin/productos" className="text-gold text-sm hover:underline">Ver todos →</Link>
          </div>
          {topProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📦</div>
              <p>Sin datos de ventas aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topProducts.map((product, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center font-bold text-gold">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{product.name}</div>
                    <div className="text-xs text-gray-500">{product.qty} vendidos</div>
                  </div>
                  <div className="w-full max-w-[100px] bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-gold h-2 rounded-full transition-all"
                      style={{ width: `${(product.qty / topProducts[0].qty) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="mt-8 bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 text-white">
        <h2 className="text-xl font-semibold mb-4">⚡ Acciones rápidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/admin/productos" className="bg-white/10 backdrop-blur p-4 rounded-xl hover:bg-white/20 transition text-center">
            <div className="text-2xl mb-1">📦</div>
            <div className="text-sm">Nuevo producto</div>
          </Link>
          <Link to="/admin/pedidos" className="bg-white/10 backdrop-blur p-4 rounded-xl hover:bg-white/20 transition text-center">
            <div className="text-2xl mb-1">📋</div>
            <div className="text-sm">Ver pedidos</div>
          </Link>
          <Link to="/admin/usuarios" className="bg-white/10 backdrop-blur p-4 rounded-xl hover:bg-white/20 transition text-center">
            <div className="text-2xl mb-1">👥</div>
            <div className="text-sm">Clientes</div>
          </Link>
          <Link to="/admin/configuracion" className="bg-white/10 backdrop-blur p-4 rounded-xl hover:bg-white/20 transition text-center">
            <div className="text-2xl mb-1">⚙️</div>
            <div className="text-sm">Configurar</div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
